import { z } from 'zod';
import { FieldTypeSchema } from './fields/registry';
import { StatusCategorySchema } from './workflow/rules';

/**
 * Import framework contracts (Bolt 9, ADR-0020): the source-neutral shapes every adapter
 * (JSM, Monday.com, CSV, and the `fixture` adapter the tests and the other Bolt 9 engineers
 * build against) discovers into, the mapping the admin fills in, and the report a dry run or
 * a real run produces. `packages/jobs/src/imports/adapter.ts` is the runtime counterpart
 * (`SourceAdapter`, `SourceTicket`); this module is the wire/DB contract only.
 */

/** `fixture` is the framework's own test/dev source (no credentials, `FixtureAdapter`); it is
 * never surfaced as a choice in the UI wizard. */
export const IMPORT_SOURCES = ['jsm', 'monday', 'csv', 'fixture'] as const;
export const ImportSourceSchema = z.enum(IMPORT_SOURCES);
export type ImportSource = z.infer<typeof ImportSourceSchema>;

/**
 * Importer provenance stamped on `ticket` / `comment` / `attachment` / `app_user` /
 * `field_definition` / `field_option` / `status` / `issue_type` / `customer_organization`
 * (rules/db.md rule 14). `id` is the source's own id; re-running upserts by `(source, id)`
 * through each table's `UNIQUE (tenant_id, external_ref->>'source', external_ref->>'id')`.
 */
export const ExternalRefSchema = z.object({
  source: z.string().min(1),
  id: z.string().min(1),
  key: z.string().optional(),
  url: z.string().optional(),
});
export type ExternalRef = z.infer<typeof ExternalRefSchema>;

export const IMPORT_STATUSES = [
  'created',
  'discovering',
  'discovered',
  'mapping',
  'dry_run',
  'ready',
  'running',
  'completed',
  'failed',
  'cancelled',
] as const;
export const ImportStatusSchema = z.enum(IMPORT_STATUSES);
export type ImportStatus = z.infer<typeof ImportStatusSchema>;

/* ----------------------------- create ----------------------------- */

/**
 * `POST /admin/v1/imports` (S-09-01). Exactly one of `projectId` / `createProjects`.
 * `credentialsSecretArn` is a Secrets Manager reference (JSM/Monday token); CSV needs none
 * (the file itself is uploaded through `POST /imports/{id}/upload`, S-09-08).
 */
export const CreateImportSchema = z
  .strictObject({
    source: ImportSourceSchema,
    projectId: z.uuid().nullable().default(null),
    createProjects: z.boolean().default(false),
    credentialsSecretArn: z.string().max(2048).nullable().default(null),
    options: z.record(z.string(), z.unknown()).default({}),
  })
  .superRefine((value, ctx) => {
    if (value.projectId && value.createProjects) {
      ctx.addIssue({
        code: 'custom',
        message: 'projectId and createProjects are mutually exclusive',
        path: ['createProjects'],
      });
    }
    if (!value.projectId && !value.createProjects) {
      ctx.addIssue({
        code: 'custom',
        message: 'one of projectId or createProjects is required',
        path: ['projectId'],
      });
    }
  });
export type CreateImport = z.infer<typeof CreateImportSchema>;

/** `GET /imports?status=`. */
export const ImportListQuerySchema = z.object({
  status: ImportStatusSchema.optional(),
});
export type ImportListQuery = z.infer<typeof ImportListQuerySchema>;

/* ----------------------------- discovery ----------------------------- */

const DiscoveredOptionSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
});

const DiscoveredFieldSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  /** Best-effort mapping onto a ServiceTim `FieldType`; `null` when the adapter cannot tell. */
  type: FieldTypeSchema.nullable(),
  options: z.array(DiscoveredOptionSchema).default([]),
});

const DiscoveredStatusSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  category: StatusCategorySchema.nullable(),
});

const DiscoveredPrioritySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
});

const DiscoveredUserSchema = z.object({
  id: z.string().min(1),
  email: z.email().nullable(),
  displayName: z.string().min(1),
});

const DiscoveredOrganizationSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
});

const DiscoveredIssueTypeSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
});

const DiscoveredProjectSchema = z.object({
  id: z.string().min(1),
  key: z.string().min(1).nullable(),
  name: z.string().min(1),
  /** Rows the adapter can see for this project/board (informational, for the wizard). */
  ticketCount: z.int().min(0).nullable().default(null),
});

/**
 * `Discovery` (S-09-02): the source-neutral schema a `SourceAdapter.discover()` returns and
 * `import_batch.discovery` stores. Every list is keyed by the *source's* id (a JSM field id,
 * a Monday column id, a CSV header) — the mapping (below) is what ties those ids to targets.
 */
export const ImportDiscoverySchema = z.object({
  projects: z.array(DiscoveredProjectSchema).default([]),
  issueTypes: z.array(DiscoveredIssueTypeSchema).default([]),
  fields: z.array(DiscoveredFieldSchema).default([]),
  statuses: z.array(DiscoveredStatusSchema).default([]),
  priorities: z.array(DiscoveredPrioritySchema).default([]),
  users: z.array(DiscoveredUserSchema).default([]),
  organizations: z.array(DiscoveredOrganizationSchema).default([]),
  /** Total records the adapter expects to iterate (informational; not a hard cap). */
  ticketCount: z.int().min(0).nullable().default(null),
});
export type ImportDiscovery = z.infer<typeof ImportDiscoverySchema>;

/* ----------------------------- mapping ----------------------------- */

/** One source field mapped onto an existing target field, a to-be-created one, or dropped. */
export const FieldMappingEntrySchema = z.union([
  z.strictObject({ fieldKey: z.string().min(1) }),
  z.strictObject({
    create: z.strictObject({
      type: FieldTypeSchema,
      label: z.string().min(1).max(80),
      /** Type-specific config, validated against `type` by `parseFieldConfig` when applied. */
      config: z.record(z.string(), z.unknown()).default({}),
    }),
  }),
  z.literal('skip'),
]);
export type FieldMappingEntry = z.infer<typeof FieldMappingEntrySchema>;

/** One source status mapped onto an existing target status, or created in the workflow draft. */
export const StatusMappingEntrySchema = z.union([
  z.strictObject({ statusId: z.uuid() }),
  z.strictObject({
    createInDraft: z.strictObject({
      name: z.string().min(1).max(80),
      category: StatusCategorySchema,
    }),
  }),
]);
export type StatusMappingEntry = z.infer<typeof StatusMappingEntrySchema>;

/** One source priority mapped onto an existing target priority, or dropped (`null`). */
export const PriorityMappingEntrySchema = z.strictObject({ priorityId: z.uuid() }).nullable();
export type PriorityMappingEntry = z.infer<typeof PriorityMappingEntrySchema>;

/** One source reporter/assignee email resolved to an existing user or a fresh imported one. */
export const UserMappingEntrySchema = z.union([
  z.strictObject({ userId: z.uuid() }),
  z.literal('create_imported'),
]);
export type UserMappingEntry = z.infer<typeof UserMappingEntrySchema>;

export const IssueTypeMappingEntrySchema = z.union([
  z.strictObject({ issueTypeId: z.uuid() }),
  z.strictObject({ create: z.strictObject({ name: z.string().min(1).max(80) }) }),
]);
export type IssueTypeMappingEntry = z.infer<typeof IssueTypeMappingEntrySchema>;

/** One source select option mapped onto an existing target option, or created on the field. */
export const OptionMappingEntrySchema = z.union([
  z.strictObject({ optionId: z.uuid() }),
  z.strictObject({ create: z.strictObject({ label: z.string().min(1).max(80) }) }),
]);
export type OptionMappingEntry = z.infer<typeof OptionMappingEntrySchema>;

/**
 * `GET/PUT /imports/{id}/mapping` (S-09-03). Every map is keyed by the source id from
 * `discovery` (field id, status id, priority id, email, issue-type id); `options` is keyed
 * `"<sourceFieldId>:<sourceOptionId>"` since option ids are only unique within their field.
 */
export const ImportMappingSchema = z.strictObject({
  fields: z.record(z.string(), FieldMappingEntrySchema).default({}),
  statuses: z.record(z.string(), StatusMappingEntrySchema).default({}),
  priorities: z.record(z.string(), PriorityMappingEntrySchema).default({}),
  users: z.record(z.email(), UserMappingEntrySchema).default({}),
  issueTypes: z.record(z.string(), IssueTypeMappingEntrySchema).default({}),
  options: z.record(z.string(), OptionMappingEntrySchema).default({}),
});
export type ImportMapping = z.infer<typeof ImportMappingSchema>;

/* ----------------------------- report ----------------------------- */

export const ImportValidationErrorSchema = z.object({
  sourceId: z.string().min(1),
  /** CSV line number, 1-based; null for API sources. */
  line: z.int().min(1).nullable().default(null),
  field: z.string().min(1),
  message: z.string().min(1),
});
export type ImportValidationError = z.infer<typeof ImportValidationErrorSchema>;

export const ImportUnmappedItemSchema = z.object({
  sourceId: z.string().min(1),
  label: z.string().min(1),
  /** How many records reference it, counted while evaluating. */
  count: z.int().min(0).default(0),
});
export type ImportUnmappedItem = z.infer<typeof ImportUnmappedItemSchema>;

export const ImportOversizedAttachmentSchema = z.object({
  sourceId: z.string().min(1),
  ticketSourceId: z.string().min(1),
  filename: z.string().min(1),
  sizeBytes: z.int().min(0),
});
export type ImportOversizedAttachment = z.infer<typeof ImportOversizedAttachmentSchema>;

export const ImportCountsSchema = z.object({
  tickets: z.int().min(0).default(0),
  comments: z.int().min(0).default(0),
  attachments: z.int().min(0).default(0),
  users: z.int().min(0).default(0),
});
export type ImportCounts = z.infer<typeof ImportCountsSchema>;

/**
 * `GET /imports/{id}/report` (S-09-04): the outcome of the last dry run (preview, nothing
 * written) or of a completed/partial run (what was actually written). `blocking` is true when
 * `validationErrors` is non-empty and the batch's `options.completePartially` is not set —
 * `POST /run` refuses to start (or resume) while it is true (S-09-05).
 */
export const ImportReportSchema = z.object({
  counts: ImportCountsSchema.default(() => ({ tickets: 0, comments: 0, attachments: 0, users: 0 })),
  unmappedFields: z.array(ImportUnmappedItemSchema).default([]),
  unmappedStatuses: z.array(ImportUnmappedItemSchema).default([]),
  unmappedUsers: z.array(ImportUnmappedItemSchema).default([]),
  validationErrors: z.array(ImportValidationErrorSchema).default([]),
  oversizedAttachments: z.array(ImportOversizedAttachmentSchema).default([]),
  blocking: z.boolean().default(false),
  generatedAt: z.iso.datetime(),
});
export type ImportReport = z.infer<typeof ImportReportSchema>;

/** `import_batch.stats`: running totals updated after every chunk (S-09-05). */
export const ImportStatsSchema = z.object({
  recordsSeen: z.int().min(0).default(0),
  ticketsCreated: z.int().min(0).default(0),
  ticketsUpdated: z.int().min(0).default(0),
  commentsCreated: z.int().min(0).default(0),
  attachmentsCopied: z.int().min(0).default(0),
  attachmentsSkipped: z.int().min(0).default(0),
  usersCreated: z.int().min(0).default(0),
  statusesCreated: z.int().min(0).default(0),
  fieldsCreated: z.int().min(0).default(0),
  errors: z.int().min(0).default(0),
  chunksCompleted: z.int().min(0).default(0),
});
export type ImportStats = z.infer<typeof ImportStatsSchema>;

/* ----------------------------- upload (CSV) ----------------------------- */

/** `POST /imports/{id}/upload` (S-09-08): presigned PUT for the CSV; the key lands on the batch. */
export const UploadRequestSchema = z.strictObject({
  filename: z.string().min(1).max(255),
  contentType: z.string().min(1).max(255).default('text/csv'),
  sizeBytes: z
    .int()
    .min(1)
    .max(200 * 1024 * 1024),
});
export type UploadRequest = z.infer<typeof UploadRequestSchema>;

export const UploadResponseSchema = z.object({
  url: z.string(),
  fields: z.record(z.string(), z.string()),
  key: z.string(),
});
export type UploadResponse = z.infer<typeof UploadResponseSchema>;

/* ----------------------------- batch DTO ----------------------------- */

/** `GET /imports/{id}` and list items (S-09-02). `credentialsSecretArn` is never returned. */
export const ImportBatchSchema = z.object({
  id: z.uuid(),
  source: ImportSourceSchema,
  projectId: z.uuid().nullable(),
  createProjects: z.boolean(),
  status: ImportStatusSchema,
  discovery: ImportDiscoverySchema.nullable(),
  mapping: ImportMappingSchema.nullable(),
  report: ImportReportSchema.nullable(),
  stats: ImportStatsSchema,
  dryRun: z.boolean(),
  options: z.record(z.string(), z.unknown()),
  createdBy: z.uuid().nullable(),
  startedAt: z.iso.datetime().nullable(),
  finishedAt: z.iso.datetime().nullable(),
  error: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ImportBatch = z.infer<typeof ImportBatchSchema>;

/** `import.completed` event data (aidlc/domain/events.md footnote). */
export const ImportCompletedDataSchema = z.object({
  batchId: z.uuid(),
  status: ImportStatusSchema,
  stats: ImportStatsSchema,
});
export type ImportCompletedData = z.infer<typeof ImportCompletedDataSchema>;

/* ----------------------------- source-neutral record shape ----------------------------- */

/**
 * Plain data types (no runtime validation — adapters own their own input parsing) shared by
 * `packages/jobs/src/imports/adapter.ts` (`SourceAdapter`, `FixtureAdapter`) and the pure dry
 * run evaluator (`packages/domain/src/imports/dry-run.ts`), which may depend on neither the
 * jobs package (DB/AWS) nor each other. Ids the adapter puts here (`issueType`, `status`,
 * `priority`, `organization`, `fields` keys) are the *source's* own ids — exactly what
 * `ImportDiscovery` listed them under — so the mapping is the only place they turn into
 * target ids (ADR-0020 §2).
 */
export interface SourceComment {
  id: string;
  authorEmail: string | null;
  body: string;
  createdAt: string;
  /** True hides the comment from customers (JSM `jsdPublic`, Monday updates default public). */
  internal: boolean;
}

export interface SourceAttachment {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  /** Opaque to the framework; only the adapter's `fetchAttachment` knows how to resolve it. */
  ref: unknown;
}

export interface SourceHistoryEntry {
  at: string;
  actorEmail: string | null;
  /** `status` for a transition, a source field id otherwise. */
  field: string;
  from: unknown;
  to: unknown;
}

export interface SourceTicket {
  /** Source key (`PROJ-123`, a Monday item id); becomes `external_ref.key`. */
  key: string;
  /** Source ticket number when the source has one (JSM issue number); reserves it verbatim. */
  number?: number | null;
  summary: string;
  description: string | null;
  /** Source issue-type id (discovery `issueTypes[].id`). */
  issueType: string;
  /** Source status id (discovery `statuses[].id`). */
  status: string;
  /** Source priority id (discovery `priorities[].id`), or null when the source has none. */
  priority?: string | null;
  reporterEmail: string | null;
  assigneeEmail?: string | null;
  /** Source organization id (discovery `organizations[].id`). */
  organization?: string | null;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string | null;
  /** By source field id (discovery `fields[].id`), raw (not yet mapped or validated). */
  fields: Record<string, unknown>;
  labels: string[];
  comments: SourceComment[];
  attachments: SourceAttachment[];
  /** Field/status changes in source-chronological order; replayed as backdated `ticket_event`s. */
  history: SourceHistoryEntry[];
  /** Source key of the parent ticket (Monday subitem → parent item), or null/absent for none. */
  parentKey?: string | null;
  /**
   * 1-based source line/row number, when the source is line-oriented (CSV) — threaded onto
   * `ImportValidationError.line` by the dry-run evaluator (S-09-08) so a bad CSV row is
   * reported with the line an admin can find in the file; absent/null for API sources (JSM,
   * Monday.com), which have no meaningful "line".
   */
  line?: number | null;
}
